import { useState } from 'react';
import { Check, ChevronDown, Copy } from 'lucide-react';
import type { Finding } from '../../core/types';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { cn } from '../../lib/cn';
import { SeverityBadge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/toastContext';

const ACCENT = {
  critical: 'border-l-critical-500',
  important: 'border-l-important-500',
  polish: 'border-l-polish-500',
} as const;

export interface FindingCardProps {
  finding: Finding;
  /** Open on first render. Used for the "start here" list. */
  defaultOpen?: boolean;
}

/**
 * One finding: what's wrong, why it costs something, and what to do about it.
 *
 * The disclosure is a plain `<button>` with `aria-expanded` and `aria-controls`
 * rather than `<details>`, because the summary row needs a copy button inside it
 * and nesting an interactive control inside `<summary>` behaves inconsistently
 * across browsers.
 *
 * The copy button copies the fix, not the title — the fix is the part someone
 * wants to paste into a notes app and work through.
 */
export function FindingCard({ finding, defaultOpen = false }: FindingCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { copy, copiedId } = useCopyToClipboard();
  const toast = useToast();
  const panelId = `finding-panel-${finding.id}`;
  const copied = copiedId === finding.id;

  const onCopy = async () => {
    const text = `${finding.title}\n\nWhy it matters: ${finding.why}\n\nFix: ${finding.fix}`;
    const ok = await copy(text, finding.id);
    if (ok) toast.success('Fix copied to your clipboard');
    else toast.error('Could not copy — select the text and copy it manually');
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-field border border-l-4 border-line bg-card',
        ACCENT[finding.severity]
      )}
    >
      <div className="flex items-start gap-2 p-3 sm:p-4">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex flex-1 items-start gap-3 text-left"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'mt-0.5 size-4 shrink-0 text-subtle transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-strong">{finding.title}</span>
            <span className="mt-1.5 flex flex-wrap items-center gap-2">
              <SeverityBadge severity={finding.severity} />
              <span className="font-mono text-[0.7rem] text-subtle">{finding.id}</span>
            </span>
          </span>
        </button>

        <button
          type="button"
          onClick={onCopy}
          className={cn(
            'shrink-0 rounded-field p-2 transition-colors',
            copied
              ? 'text-success-600 dark:text-success-400'
              : 'text-subtle hover:bg-sunken hover:text-strong'
          )}
        >
          <span className="sr-only-live">
            {copied ? 'Copied' : `Copy the fix for ${finding.title}`}
          </span>
          {copied ? (
            <Check aria-hidden="true" className="size-4" />
          ) : (
            <Copy aria-hidden="true" className="size-4" />
          )}
        </button>
      </div>

      {/* Kept mounted but hidden, so browser find-in-page still reaches the
          text of a collapsed finding. */}
      <div
        id={panelId}
        hidden={!open}
        className="border-t border-line bg-sunken px-4 py-3.5 sm:px-5"
      >
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-xs font-semibold tracking-wide text-subtle uppercase">
              Why it matters
            </dt>
            <dd className="mt-1 leading-relaxed text-muted">{finding.why}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold tracking-wide text-subtle uppercase">
              How to fix it
            </dt>
            <dd className="mt-1 leading-relaxed text-strong">{finding.fix}</dd>
          </div>
          {finding.evidence?.length ? (
            <div>
              <dt className="text-xs font-semibold tracking-wide text-subtle uppercase">
                Found in your profile
              </dt>
              <dd className="mt-1.5 flex flex-wrap gap-1.5">
                {finding.evidence.map((item) => (
                  <code
                    key={item}
                    className="rounded bg-card px-1.5 py-0.5 font-mono text-xs text-muted ring-1 ring-line ring-inset"
                  >
                    {item}
                  </code>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </div>
  );
}
